import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNewTranslation } from 'hooks'
import {
    selectAllTemplates,
    selectFilteredTemplates,
    CampaignTemplateState,
    OBJECTIVE,
    ObjectiveType,
    setTemplateFilter,
    selectTemplatesFilter,
    selectTemplateData,
    CampaignTemplateStep,
} from 'state/campaignTemplates'
import { selectRequestsAreLoading } from 'state'
import { STATE_KEYS } from 'state/requests'
import { ROUTES } from 'components/Root'
import styled from 'particles/themes/styled'
import RectangleButton from 'particles/Button/RectangleButton'
import TemplateCard from './TemplateCard'
import ReminderAlert from 'particles/Alert/ReminderAlert'
import Loading from '../Loading'
import { TemplateContentContainer } from '../style'
import AllTemplateModal from './AllTemplateModal'
import CommentContainer, { fieldIdentifiers } from 'components/Shared/Comment'
import Label from 'particles/Label'
import EditTemplateRestricted from 'components/Shared/Permission/EditTemplateRestricted'
import TemplateInputFieldContainer from 'components/Shared/Permission/TemplateInputFieldContainer'
import { CAMPAIGN_STATUS_ENUM } from 'state/campaigns'

interface TemplateSelectionProps {
    backPreviousSection: () => void
    currentTemplateUuid?: string
}

const TemplateSelection: React.FC<TemplateSelectionProps> = ({ backPreviousSection, currentTemplateUuid }) => {
    const [t] = useNewTranslation()
    const dispatch = useDispatch()
    const [isShowWarningAlert, setIsShowWarningAlert] = useState<boolean>(false)
    const [templates, setTemplates] = useState<CampaignTemplateState[]>([])
    const [selectedTemplateUuid, setSelectedTemplateUuid] = useState<string>()
    const isLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.GetCampaignTemplates]))
    const allTemplates = useSelector(selectAllTemplates)
    const filteredTemplates = useSelector(selectFilteredTemplates)
    const templateFilter = useSelector(selectTemplatesFilter)
    const templateData = useSelector(selectTemplateData)

    useEffect(() => {
        const selectedTemplate = allTemplates.find(template => template.uuid === currentTemplateUuid)
        let template = []
        if (filteredTemplates.length === 0) {
            let allTemplateWithinObjectives = allTemplates.filter(
                value => value.objective === (templateFilter.objective ?? templateData?.objective),
            )
            if (templateFilter.objective !== OBJECTIVE.appPromotion || !allTemplateWithinObjectives.length) {
                setIsShowWarningAlert(true)
            }
            if (selectedTemplate) {
                allTemplateWithinObjectives = allTemplateWithinObjectives.filter(
                    template => template.uuid !== currentTemplateUuid,
                )
                allTemplateWithinObjectives.unshift(selectedTemplate)
            }
            template = allTemplateWithinObjectives
        } else {
            setIsShowWarningAlert(false)
            let filteredTemplateCopy = [...filteredTemplates]
            if (selectedTemplate) {
                filteredTemplateCopy = filteredTemplateCopy.filter(template => template.uuid !== currentTemplateUuid)
                filteredTemplateCopy.unshift(selectedTemplate)
            }
            template = filteredTemplateCopy
        }
        setTemplates(template)
    }, [allTemplates, filteredTemplates])

    return (
        <TemplateSelectionContainer data-testid="test-template-selection-page">
            <div className="form-title">
                <CommentContainer
                    identifier={`${CampaignTemplateStep.TEMPLATE_SELECTION}_${fieldIdentifiers.templateSelection}`}
                >
                    <Label title={t('campaign.template.step1.templateSelection.title')} size="xxl" isRequired={false} />
                </CommentContainer>
            </div>
            {isLoading && <Loading title={t('campaign.template.step1.templateSelection.loadingText')} />}

            {!isLoading && isShowWarningAlert && !currentTemplateUuid && (
                <ReminderAlert
                    form={'warning'}
                    title={t('campaign.template.step1.templateSelection.noTemplateWarning.title')}
                    subtext={t('campaign.template.step1.templateSelection.noTemplateWarning.subtext')}
                    link={{
                        label: t('campaign.template.alertReminder.createCustomizeCampaign.button.text'),
                        to: `${ROUTES.Campaign}/edit/registration`,
                    }}
                    isClosable={false}
                    width={'100%'}
                />
            )}
            {!isLoading && (
                <div className="card-list">
                    {templates.map((template: CampaignTemplateState) => (
                        <TemplateInputFieldContainer key={template.uuid}>
                            {(isReadOnly: boolean) => {
                                const CardComponent = (
                                    <TemplateCard
                                        title={t(template.name)}
                                        description={t(template.description)}
                                        subTitle={t(
                                            'campaign.template.step1.templateSelection.templateCard.durationParticipants.text',
                                            {
                                                duration: template.duration,
                                                min: template.kpi_range.min,
                                                max: template.kpi_range.max,
                                            },
                                        )}
                                        icon={
                                            Object.entries(OBJECTIVE).find(
                                                ([key, val]) => val === template.objective,
                                            )?.[0] as ObjectiveType
                                        }
                                        isSelected={template.uuid === selectedTemplateUuid}
                                        isCurrentTemplate={template.uuid === currentTemplateUuid}
                                        onClick={() => {
                                            setSelectedTemplateUuid(template.uuid)
                                            dispatch(
                                                setTemplateFilter({
                                                    templateUuid: template.uuid,
                                                }),
                                            )
                                        }}
                                        showButton={
                                            template.uuid == selectedTemplateUuid &&
                                            template.uuid !== currentTemplateUuid
                                        }
                                        isReadOnly={isReadOnly}
                                        dataTestId={`test-${template.name}`}
                                    />
                                )
                                if (isReadOnly) {
                                    if (template.uuid === currentTemplateUuid) {
                                        return CardComponent
                                    } else {
                                        return null
                                    }
                                } else {
                                    return CardComponent
                                }
                            }}
                        </TemplateInputFieldContainer>
                    ))}
                </div>
            )}
            <EditTemplateRestricted statusesToShow={[CAMPAIGN_STATUS_ENUM.FOR_REVIEW]}>
                <div className="see-all-template-button">
                    <AllTemplateModal
                        templates={allTemplates}
                        currentTemplateUuid={currentTemplateUuid}
                        selectedTemplateUuid={selectedTemplateUuid}
                        setSelectedTemplateUuid={setSelectedTemplateUuid}
                    />
                </div>
            </EditTemplateRestricted>

            <EditTemplateRestricted checkForLiveEditBriefAndDeadline statusesToShow={[CAMPAIGN_STATUS_ENUM.LIVE]}>
                <div>
                    <RectangleButton
                        type={'button'}
                        form={'outlined'}
                        size={'l'}
                        label={t('button.back')}
                        onClick={backPreviousSection}
                    />
                </div>
            </EditTemplateRestricted>
        </TemplateSelectionContainer>
    )
}

export const TemplateSelectionContainer = styled(TemplateContentContainer)`
    max-width: 770px;
    .card-list {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(377px, 1fr));
        gap: ${({ theme }): string => theme.spacing[400]};
    }

    .see-all-template-button {
        width: fit-content;
    }
`

export default TemplateSelection
